/**
 * The one mid-page tuition offer on a school page.
 *
 * What: A slim band under the school's cut-off and About cards — a short line,
 * a single trial button, and a sentence saying the page continues.
 * Why: A parent who came for the cut-off figure has what they wanted by this
 * point, so the offer belongs here. The full DeepThink band is deliberately far
 * below, where it cannot interrupt the school content. Two properties keep this
 * a bridge rather than another wall: it carries no heading, so it does not read
 * as the start of a new section or enter the heading outline, and its closing
 * clause names what is still to come, which is what stops a band this far up
 * the page from being mistaken for the floor. The demo link is dropped for the
 * same reason — a second exit competes with a page the reader hasn't finished.
 * When: Update when the mid-page conversion copy or the trial entry changes.
 */
import React from 'react';
import { Paper, Stack, Typography } from '@mui/material';

import LandingSectionShell from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens';
import LandingTrialCtas from '@/components/landing/LandingTrialCtas';
import { MAX_CLASS_SIZE } from '@/content/classSizeContent';

/**
 * Anchor id. Carries no nav chip — the band has no heading to label one with.
 *
 * Not a scroll-reveal key: it sits inside the school run, which paints on
 * arrival, and a single animating band between two static ones is the dead spot
 * in reverse — see the note above `support` in `sectionRevealMap`.
 */
export const SCHOOL_TRIAL_CALLOUT_ID = 'school-trial-callout';

interface SchoolTrialCalloutProps {
  schoolName: string;
  /** Attribution tag carried into the trial lead (CAPI + reporting). */
  sourceLocation: string;
}

function SchoolTrialCallout({
  schoolName,
  sourceLocation,
}: SchoolTrialCalloutProps) {
  const label = `Math tuition alongside ${schoolName}`;

  return (
    <LandingSectionShell
      id={SCHOOL_TRIAL_CALLOUT_ID}
      ariaLabel={label}
      // Spacing between these sections comes from the page-level stack.
      mb={0}
      sx={{ width: '100%' }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: radii.md,
          backgroundColor: colors.paper,
          border: `1px solid ${colors.border}`,
          boxShadow: shadows.soft,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 3 }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={0.75} sx={{ maxWidth: 640 }}>
            <Typography
              sx={{
                fontSize: { xs: '1.0625rem', sm: '1.125rem' },
                fontWeight: 800,
                color: colors.ink,
                lineHeight: 1.35,
                letterSpacing: '-0.01em',
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.9375rem', sm: '1rem' },
                color: colors.inkMuted,
                lineHeight: 1.65,
              }}
            >
              {`Classes capped at ${MAX_CLASS_SIZE} students, grouped by learning pace and matched to your child's level. Or keep reading — the rest of this page covers ${schoolName} in detail, including admissions and FAQs.`}
            </Typography>
          </Stack>
          <LandingTrialCtas
            sourceLocation={sourceLocation}
            showSecondary={false}
            sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}
          />
        </Stack>
      </Paper>
    </LandingSectionShell>
  );
}

export default SchoolTrialCallout;
